
import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import PropertyCard from "@/components/common/PropertyCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

type Property = {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url: string | null;
  created_at: string | null;
  status: string;
  closed_date: string | null;
  sale_price: number | null;
};

const ClosedDealsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchClosedProperties = async () => {
      try {
        setIsLoading(true);
        
        if (!user) return;
        
        const { data, error } = await supabase
          .from('properties')
          .select('*')
          .eq('seller_id', user.id)
          .in('status', ['sold', 'closed']);
          
        if (error) {
          console.error("Error fetching closed properties:", error);
          toast({
            title: "Error",
            description: "Failed to load your closed deals",
            variant: "destructive"
          });
          return;
        }
        
        setProperties(data || []);
      } catch (error) {
        console.error("Unexpected error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClosedProperties();
  }, [user, toast]);
  
  // Format properties to match the PropertyCard component props
  const formattedProperties = properties.map(property => ({
    id: property.id,
    address: property.address,
    city: property.city,
    state: property.state,
    zip: property.zip_code,
    price: property.sale_price || property.price, // Use sale_price if available, otherwise original price
    bedrooms: property.bedrooms,
    bathrooms: property.bathrooms,
    squareFeet: property.square_feet,
    imageUrl: property.image_url || "https://images.unsplash.com/photo-1570129477492-45c003edd2be",
    daysOnSite: property.created_at && property.closed_date 
      ? Math.ceil((new Date(property.closed_date).getTime() - new Date(property.created_at).getTime()) / (1000 * 3600 * 24))
      : 0,
    status: "closed" as const
  }));
  
  // Filter properties based on search query
  const filteredProperties = formattedProperties.filter(property => {
    const searchLower = searchQuery.toLowerCase();
    return (
      property.address.toLowerCase().includes(searchLower) ||
      property.city.toLowerCase().includes(searchLower) ||
      property.state.toLowerCase().includes(searchLower) ||
      property.zip.toLowerCase().includes(searchLower)
    );
  });
  
  // Sort properties
  const sortedProperties = [...filteredProperties].sort((a, b) => {
    if (sortBy === "recent") {
      return b.daysOnSite - a.daysOnSite;
    } else if (sortBy === "price-high") {
      return b.price - a.price;
    } else if (sortBy === "price-low") {
      return a.price - b.price;
    }
    return 0;
  });
  
  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold tracking-tight">Closed Deals</h1>
      
      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search closed deals..."
            className="pl-8 bg-muted"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[180px] bg-muted">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most Recent</SelectItem>
            <SelectItem value="price-high">Price: High to Low</SelectItem>
            <SelectItem value="price-low">Price: Low to High</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Property listing */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading closed deals...</p>
        </div>
      ) : sortedProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedProperties.map((property) => (
            <PropertyCard key={property.id} {...property} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No closed deals found.</p>
        </div>
      )}
      
      {/* Stats summary */}
      <div className="bg-card rounded-lg p-6 mt-8">
        <h2 className="text-lg font-semibold mb-4">Closed Deals Summary</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-muted-foreground text-sm">Total Closed</p>
            <p className="text-2xl font-bold">{properties.length}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">Total Volume</p>
            <p className="text-2xl font-bold">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
              }).format(properties.reduce((sum, prop) => sum + (prop.sale_price || prop.price), 0))}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">Avg. Price</p>
            <p className="text-2xl font-bold">
              {properties.length > 0 ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
              }).format(properties.reduce((sum, prop) => sum + (prop.sale_price || prop.price), 0) / properties.length) : '$0'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClosedDealsPage;
